import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Input,
  Field,
  tokens,
} from '@fluentui/react-components';

interface CreateBoardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  boardName: string;
  setBoardName: (name: string) => void;
}

export const CreateBoardDialog: React.FC<CreateBoardDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  boardName,
  setBoardName,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Create New Board</DialogTitle>
        <DialogBody>
          <DialogContent style={{ marginBottom: '1rem' }}>
            <Field label="Board Name" required>
              <Input
                value={boardName}
                onChange={(_, data) => setBoardName(data.value)}
                placeholder="Enter board name"
                autoFocus
              />
            </Field>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={onConfirm}
            disabled={!boardName.trim()}
          >
            Create Board
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface RenameBoardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  boardName: string;
  setBoardName: (name: string) => void;
}

export const RenameBoardDialog: React.FC<RenameBoardDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  boardName,
  setBoardName,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Rename Board</DialogTitle>
        <DialogBody>
          <DialogContent style={{ marginBottom: '1rem' }}>
            <Field label="Board Name" required>
              <Input
                value={boardName}
                onChange={(_, data) => setBoardName(data.value)}
                placeholder="Enter board name"
                autoFocus
              />
            </Field>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={onConfirm}
            disabled={!boardName.trim()}
          >
            Rename Board
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteBoardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  boardName: string;
  taskCount: number;
}

export const DeleteBoardDialog: React.FC<DeleteBoardDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  boardName,
  taskCount,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Delete Board</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                Are you sure you want to delete the "{boardName}" board?
              </Text>
              {taskCount > 0 && (
                <Text style={{ color: tokens.colorPaletteRedForeground1, fontWeight: 'semibold' }}>
                  This will permanently delete {taskCount} task{taskCount > 1 ? 's' : ''}.
                </Text>
              )}
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                This action cannot be undone.
              </Text>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Delete Board
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface ColumnMoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  columnTitle: string;
}

export const ColumnMoveDialog: React.FC<ColumnMoveDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  columnTitle,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => {
      if (!data.open) {
        onClose();
      }
    }}>
      <DialogSurface>
        <DialogTitle>Move Column</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text>
              Are you sure you want to move the "{columnTitle}" column to this new position?
            </Text>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Move Column
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteCardsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemType: 'column' | 'row';
  itemName: string;
  taskCount: number;
}

export const DeleteCardsDialog: React.FC<DeleteCardsDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemType,
  itemName,
  taskCount,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Delete Tasks</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>
                Are you sure you want to delete {taskCount} task{taskCount !== 1 ? 's' : ''} from the "{itemName}" {itemType}?
              </Text>
              <Text size={300} style={{ color: tokens.colorPaletteRedForeground1 }}>
                This action cannot be undone.
              </Text>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Delete {taskCount} Task{taskCount !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteCommentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteCommentDialog: React.FC<DeleteCommentDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text>
              Are you sure you want to delete this comment? This action cannot be undone.
            </Text>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Delete Comment
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface DeleteAttachmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteAttachmentDialog: React.FC<DeleteAttachmentDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text>
              Are you sure you want to delete this attachment? This action cannot be undone.
            </Text>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={onConfirm}>
            Delete Attachment
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
